import { GameAudio } from "./audio";
import { COLORS, GAME_CONFIG } from "./config";
import { InputController } from "./input";
import { CanvasRenderer } from "./renderer";
import type { GameConfig, GameState, Player, RenderSnapshot, ScoreState, Vehicle, VehicleType } from "./types";
import type { GameUI } from "../ui/game-ui";

const BEST_SCORE_KEY = "istanbul-highway-flow-best";
const PLAYER_LENGTH = 92;
const MAX_LIVES = 3;
const DAMAGE_COOLDOWN = 1.05;

interface LeadVehicle {
  speed: number;
  length: number;
  gap: number;
}

export class HighwayGame {
  private readonly config: GameConfig = GAME_CONFIG;
  private readonly input = new InputController();
  private readonly renderer: CanvasRenderer;
  private readonly audio = new GameAudio();
  private state: GameState = "menu";
  private player: Player;
  private vehicles: Vehicle[] = [];
  private score: ScoreState;
  private lanePressure = [0, 0, 0, 0];
  private animationId = 0;
  private lastTime = 0;
  private spawnTimer = 0.2;
  private roadOffset = 0;
  private elapsed = 0;
  private nextVehicleId = 1;
  private flash = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly ui: GameUI
  ) {
    this.renderer = new CanvasRenderer(canvas, this.config);
    this.player = this.createPlayer();
    this.score = this.createScore();

    window.addEventListener("resize", () => {
      this.renderer.resize();
      this.player.visualX = this.renderer.laneCenter(this.player.lane);
    });
  }

  boot(): void {
    this.reset(false);
    this.ui.setMuted(this.audio.isMuted);
    this.ui.setState(this.state, this.score);
    this.lastTime = performance.now();
    this.animationId = window.requestAnimationFrame(this.loop);
  }

  start(): void {
    if (this.state !== "menu") return;
    void this.audio.unlock();
    this.reset(true);
    this.setState("playing");
  }

  restart(): void {
    if (this.state !== "gameOver") return;
    void this.audio.unlock();
    this.reset(true);
    this.setState("playing");
  }

  setMuted(muted: boolean): void {
    this.audio.setMuted(muted);
    this.ui.setMuted(muted);
  }

  getSnapshot(): RenderSnapshot {
    return {
      state: this.state,
      player: this.player,
      vehicles: this.vehicles,
      score: this.score,
      lanePressure: this.lanePressure,
      roadOffset: this.roadOffset,
      elapsed: this.elapsed,
      flash: this.flash
    };
  }

  private loop = (time: number): void => {
    const dt = Math.min(0.033, Math.max(0, (time - this.lastTime) / 1000));
    this.lastTime = time;
    this.update(dt);
    this.renderer.draw(this.getSnapshot());
    this.animationId = window.requestAnimationFrame(this.loop);
  };

  private update(dt: number): void {
    const actions = this.input.consumeActions();

    if (this.state === "menu" && actions.start) {
      this.start();
    }

    if (this.state === "gameOver" && actions.restart) {
      this.restart();
    }

    if ((this.state === "playing" || this.state === "paused") && actions.pause) {
      this.setState(this.state === "playing" ? "paused" : "playing");
    }

    if (this.state !== "playing") {
      this.audio.updateEngine(0);
      return;
    }

    this.elapsed += dt;
    this.flash = Math.max(0, this.flash - dt * 2.8);
    this.player.damageCooldown = Math.max(0, this.player.damageCooldown - dt);
    this.updateLaneChange(actions.laneDelta, dt);
    this.updateSpeed(actions.boost, dt);
    this.updateTraffic(dt);
    this.updateScoring(dt);
    this.updateLanePressure();
    this.checkCollisions();

    this.roadOffset += this.player.speed * dt * 0.72;
    this.audio.updateEngine(this.player.speed / this.config.maxSpeed);
    this.ui.updateHud(
      this.speedKmh(),
      this.score,
      this.lanePressure,
      this.player.lives,
      this.player.maxLives
    );
  }

  private updateLaneChange(laneDelta: -1 | 0 | 1, dt: number): void {
    const isChanging = this.player.lane !== this.player.targetLane;

    if (laneDelta !== 0 && !isChanging) {
      const nextLane = this.player.lane + laneDelta;
      if (nextLane >= 0 && nextLane < this.config.laneCount) {
        if (this.isLaneSafe(nextLane)) {
          this.player.targetLane = nextLane;
          this.player.laneTransition = 0;
          this.audio.laneTick();
        } else {
          this.score.penalties += 1;
          this.score.combo = 1;
          this.player.speed = Math.max(this.config.minSpeed, this.player.speed - 80);
          this.applyDamage(0.84, 95);
          this.audio.warning();
        }
      }
    }

    if (this.player.lane !== this.player.targetLane) {
      this.player.laneTransition = Math.min(
        1,
        this.player.laneTransition + dt / this.config.laneChangeDuration
      );
      const fromX = this.renderer.laneCenter(this.player.lane);
      const toX = this.renderer.laneCenter(this.player.targetLane);
      const t = this.easeOutCubic(this.player.laneTransition);
      this.player.visualX = fromX + (toX - fromX) * t;

      if (this.player.laneTransition >= 1) {
        this.player.lane = this.player.targetLane;
        this.player.visualX = this.renderer.laneCenter(this.player.lane);
      }
    } else {
      this.player.visualX = this.renderer.laneCenter(this.player.lane);
    }
  }

  private updateSpeed(boostRequested: boolean, dt: number): void {
    const input = this.input.state;
    const ahead = this.closestAheadInLane(this.player.lane);
    let speedLimit = this.config.maxSpeed;

    if (ahead && ahead.gap < 330) {
      const pressure = 1 - ahead.gap / 330;
      speedLimit = Math.min(speedLimit, ahead.vehicle.speed + 70 - pressure * 210);
      if (ahead.gap < 118) {
        this.score.combo = 1;
        this.score.penalties += dt > 0 ? 0.012 : 0;
      }
    }

    if (boostRequested && this.player.boostCooldown <= 0) {
      this.player.boostTimer = this.config.boostDuration;
      this.player.boostCooldown = this.config.boostCooldown;
      this.audio.bonus();
    }

    this.player.boostCooldown = Math.max(0, this.player.boostCooldown - dt);
    this.player.boostTimer = Math.max(0, this.player.boostTimer - dt);

    let driverTarget = this.config.cruiseSpeed;
    let accelerationRate = this.config.acceleration * 0.38;
    let brakingRate = this.config.coastDeceleration;

    if (input.accelerate) {
      driverTarget = this.config.maxSpeed;
      accelerationRate = this.config.acceleration;
      brakingRate = this.config.coastDeceleration;
    } else if (input.brake) {
      driverTarget = this.config.minSpeed;
      accelerationRate = this.config.acceleration * 0.25;
      brakingRate = this.config.braking;
    } else if (this.player.speed > this.config.cruiseSpeed) {
      driverTarget = this.config.cruiseSpeed;
      brakingRate = this.config.coastDeceleration;
    }

    if (input.brake && this.player.speed > this.config.cruiseSpeed * 0.72) {
      this.score.combo = 1;
      this.score.penalties += dt * 0.08;
    }

    if (this.player.boostTimer > 0) {
      driverTarget = Math.min(this.config.maxSpeed + this.config.boostSpeed * 0.42, driverTarget + this.config.boostSpeed);
      accelerationRate += this.config.boostSpeed * 0.9;
    }

    const cappedTarget = Math.min(driverTarget, speedLimit);
    const followBrake = this.player.speed > speedLimit ? this.config.braking * 1.2 : brakingRate;
    this.player.speed = this.approachSpeed(this.player.speed, cappedTarget, accelerationRate, followBrake, dt);

    this.player.speed = this.clamp(
      this.player.speed,
      this.config.minSpeed,
      this.config.maxSpeed + (this.player.boostTimer > 0 ? this.config.boostSpeed * 0.42 : 0)
    );
  }

  private updateTraffic(dt: number): void {
    this.spawnTimer -= dt;
    if (this.spawnTimer <= 0) {
      this.trySpawnVehicle();
      const difficulty = Math.min(1.8, 1 + this.elapsed * this.config.difficultyRamp);
      const interval = this.clamp(
        this.config.baseSpawnInterval / difficulty,
        this.config.minSpawnInterval,
        this.config.baseSpawnInterval
      );
      this.spawnTimer = interval * (0.72 + Math.random() * 0.66);
    }

    for (const vehicle of this.vehicles) {
      const carAhead = this.closestAheadForVehicle(vehicle);
      const playerAhead = this.playerAheadForVehicle(vehicle);
      vehicle.reactionTimer -= dt;
      if (vehicle.reactionTimer <= 0) {
        vehicle.reactionTimer = vehicle.reactionDelay;
        vehicle.targetSpeed = this.behaviorTargetSpeed(vehicle);
      }

      const followLimit = Math.min(
        this.followingSpeedLimit(vehicle, carAhead),
        this.followingSpeedLimit(vehicle, playerAhead)
      );
      const effectiveTarget = Math.min(vehicle.targetSpeed, followLimit);
      const emergencyGap = this.tightestLeadGap(carAhead, playerAhead);
      const brakeRate =
        emergencyGap !== null && emergencyGap < vehicle.length + this.config.trafficMinGap * 0.75
          ? this.config.trafficEmergencyBraking
          : this.config.trafficBraking;
      vehicle.speed = this.approachSpeed(
        vehicle.speed,
        effectiveTarget,
        this.config.trafficAcceleration,
        brakeRate,
        dt
      );
      vehicle.y += (this.player.speed - vehicle.speed) * dt;
    }

    this.enforceTrafficSpacing();
    this.enforcePlayerRearSpacing();

    for (const vehicle of this.vehicles) {
      const playerY = this.renderer.playerY();
      if (!vehicle.passed && vehicle.y > playerY + vehicle.length * 0.72) {
        vehicle.passed = true;
        this.score.overtakes += 1;
        this.score.combo = Math.min(9, this.score.combo + 1);
        this.score.score += 80 * this.score.combo;
        if (this.score.combo >= 4) this.audio.bonus();
      }

      const adjacent = Math.abs(vehicle.lane - this.player.lane) === 1;
      const closeY = Math.abs(vehicle.y - playerY) < 72;
      if (!vehicle.nearMissed && adjacent && closeY) {
        vehicle.nearMissed = true;
        this.score.nearMisses += 1;
        this.score.score += 160 * this.score.combo;
        this.audio.bonus();
      }
    }

    this.vehicles = this.vehicles.filter((vehicle) => vehicle.y < this.renderer.playerY() + 420);
  }

  private updateScoring(dt: number): void {
    const speedRatio = this.player.speed / this.config.maxSpeed;
    this.score.distance += this.player.speed * dt;
    this.score.score += (12 + speedRatio * 36) * dt * this.score.combo;

    if (speedRatio > 0.72) {
      this.score.score += 18 * dt * this.score.combo;
    }
  }

  private updateLanePressure(): void {
    const playerY = this.renderer.playerY();
    for (let lane = 0; lane < this.config.laneCount; lane += 1) {
      const vehiclesAhead = this.vehicles.filter(
        (vehicle) => vehicle.lane === lane && vehicle.y < playerY && playerY - vehicle.y < 580
      );
      const pressure = vehiclesAhead.reduce((sum, vehicle) => {
        const gap = playerY - vehicle.y;
        const slowFactor = 1 - vehicle.speed / this.config.maxSpeed;
        return sum + Math.max(0, 1 - gap / 580) * (0.45 + slowFactor);
      }, 0);
      this.lanePressure[lane] = this.clamp(pressure, 0, 1);
    }
  }

  private checkCollisions(): void {
    const playerY = this.renderer.playerY();
    const playerWidth = 46;
    const playerLength = 92;

    for (const vehicle of this.vehicles) {
      const vehicleX = this.renderer.laneCenter(vehicle.lane);
      const overlapX = Math.abs(vehicleX - this.player.visualX) < (vehicle.width + playerWidth) / 2 - this.config.collisionMargin;
      const overlapY = Math.abs(vehicle.y - playerY) < (vehicle.length + playerLength) / 2 - this.config.collisionMargin;
      if (overlapX && overlapY) {
        this.score.penalties += 1;
        this.score.combo = 1;
        if (this.applyDamage(1, 180)) {
          vehicle.y = playerY + vehicle.length + PLAYER_LENGTH + this.config.trafficMinGap;
          vehicle.speed = Math.min(vehicle.speed, Math.max(this.config.minSpeed, this.player.speed - 120));
          vehicle.targetSpeed = Math.min(vehicle.targetSpeed, vehicle.speed);
          this.audio.crash();
        }
        return;
      }
    }
  }

  private trySpawnVehicle(): void {
    const candidateLanes = [0, 1, 2, 3].sort(() => Math.random() - 0.5);
    const lane = candidateLanes.find((candidate) => this.canSpawnInLane(candidate));

    if (lane === undefined) return;

    const behaviorRoll = Math.random();
    const behavior =
      behaviorRoll > 0.9 ? "blocker" : behaviorRoll > 0.7 ? "hesitant" : behaviorRoll > 0.48 ? "slow" : "steady";
    const baseSpeed =
      behavior === "blocker"
        ? 210 + Math.random() * 80
        : behavior === "slow"
          ? 260 + Math.random() * 120
          : behavior === "hesitant"
            ? 330 + Math.random() * 240
            : 420 + Math.random() * 260;
    const type = this.pickVehicleType();
    const dimensions = this.vehicleDimensions(type);

    const y = this.spawnYForLane(lane);

    this.vehicles.push({
      id: this.nextVehicleId,
      lane,
      y,
      speed: baseSpeed,
      targetSpeed: baseSpeed,
      length: dimensions.length,
      width: dimensions.width,
      type,
      reactionDelay:
        behavior === "hesitant"
          ? 0.65 + Math.random() * 0.55
          : behavior === "blocker"
            ? 1.2 + Math.random() * 0.8
            : 0.9 + Math.random() * 0.8,
      reactionTimer: Math.random() * 0.6,
      behavior,
      color: COLORS[this.nextVehicleId % COLORS.length],
      passed: false,
      nearMissed: false
    });
    this.nextVehicleId += 1;
  }

  private isLaneSafe(lane: number): boolean {
    const playerY = this.renderer.playerY();
    return this.vehicles.every((vehicle) => {
      if (vehicle.lane !== lane) return true;
      const delta = vehicle.y - playerY;
      if (delta < 0) return Math.abs(delta) > this.config.safeForwardGap;
      return delta > this.config.safeBackGap;
    });
  }

  private closestAheadInLane(lane: number): { vehicle: Vehicle; gap: number } | null {
    const playerY = this.renderer.playerY();
    let closest: { vehicle: Vehicle; gap: number } | null = null;
    for (const vehicle of this.vehicles) {
      if (vehicle.lane !== lane || vehicle.y >= playerY) continue;
      const gap = playerY - vehicle.y;
      if (!closest || gap < closest.gap) {
        closest = { vehicle, gap };
      }
    }
    return closest;
  }

  private closestAheadForVehicle(subject: Vehicle): LeadVehicle | null {
    let closest: LeadVehicle | null = null;
    for (const vehicle of this.vehicles) {
      if (vehicle === subject || vehicle.lane !== subject.lane || vehicle.y >= subject.y) continue;
      const gap = subject.y - vehicle.y;
      if (!closest || gap < closest.gap) {
        closest = { speed: vehicle.speed, length: vehicle.length, gap };
      }
    }
    return closest;
  }

  private playerAheadForVehicle(subject: Vehicle): LeadVehicle | null {
    const playerY = this.renderer.playerY();
    if (subject.y <= playerY || !this.playerOccupiesLane(subject.lane)) {
      return null;
    }

    return {
      speed: this.player.speed,
      length: PLAYER_LENGTH,
      gap: subject.y - playerY
    };
  }

  private playerOccupiesLane(lane: number): boolean {
    if (lane === this.player.lane || lane === this.player.targetLane) {
      return true;
    }

    const layout = this.renderer.getLayout();
    return Math.abs(layout.centers[lane] - this.player.visualX) < layout.laneWidth * 0.56;
  }

  private behaviorTargetSpeed(vehicle: Vehicle): number {
    let target: number;
    if (vehicle.behavior === "blocker") {
      target = 210 + Math.sin(this.elapsed * 0.8 + vehicle.id) * 26;
    } else if (vehicle.behavior === "slow") {
      target = 275 + Math.random() * 120;
    } else if (vehicle.behavior === "hesitant") {
      target = vehicle.targetSpeed + (Math.random() - 0.56) * 130;
    } else {
      target = vehicle.targetSpeed + (Math.random() - 0.46) * 55;
    }

    const typeLimit =
      vehicle.type === "bus"
        ? 0.64
        : vehicle.type === "van"
          ? 0.74
          : vehicle.type === "suv"
            ? 0.82
            : 0.92;

    return this.clamp(target, this.config.minSpeed, this.config.maxSpeed * typeLimit);
  }

  private pickVehicleType(): VehicleType {
    const roll = Math.random();
    if (roll < 0.18) return "hatchback";
    if (roll < 0.56) return "sedan";
    if (roll < 0.78) return "suv";
    if (roll < 0.93) return "van";
    return "bus";
  }

  private vehicleDimensions(type: VehicleType): { length: number; width: number } {
    if (type === "hatchback") {
      return { length: 72 + Math.random() * 10, width: 40 + Math.random() * 5 };
    }
    if (type === "sedan") {
      return { length: 88 + Math.random() * 12, width: 43 + Math.random() * 5 };
    }
    if (type === "suv") {
      return { length: 94 + Math.random() * 12, width: 48 + Math.random() * 6 };
    }
    if (type === "van") {
      return { length: 106 + Math.random() * 14, width: 50 + Math.random() * 6 };
    }
    return { length: 138 + Math.random() * 18, width: 56 + Math.random() * 5 };
  }

  private followingSpeedLimit(
    vehicle: Vehicle,
    lead: LeadVehicle | null
  ): number {
    if (!lead) return this.config.maxSpeed;

    const visualGap = (vehicle.length + lead.length) / 2;
    const desiredGap = visualGap + this.config.trafficMinGap + vehicle.speed * this.config.trafficTimeGap;
    const closingSpeed = Math.max(0, vehicle.speed - lead.speed);

    if (lead.gap < visualGap + 24) {
      return Math.max(this.config.minSpeed, lead.speed - 170);
    }

    if (lead.gap < desiredGap) {
      const pressure = 1 - lead.gap / desiredGap;
      return Math.max(this.config.minSpeed, lead.speed - pressure * 210 - closingSpeed * 0.45);
    }

    return this.config.maxSpeed;
  }

  private tightestLeadGap(...leads: Array<LeadVehicle | null>): number | null {
    const gaps = leads
      .filter((lead): lead is LeadVehicle => lead !== null)
      .map((lead) => lead.gap);

    return gaps.length > 0 ? Math.min(...gaps) : null;
  }

  private enforceTrafficSpacing(): void {
    for (let lane = 0; lane < this.config.laneCount; lane += 1) {
      const laneVehicles = this.vehicles
        .filter((vehicle) => vehicle.lane === lane)
        .sort((a, b) => a.y - b.y);

      for (let index = laneVehicles.length - 2; index >= 0; index -= 1) {
        const ahead = laneVehicles[index];
        const behind = laneVehicles[index + 1];
        const minGap =
          (ahead.length + behind.length) / 2 +
          this.config.trafficMinGap +
          Math.max(0, behind.speed - ahead.speed) * 0.16;

        if (behind.y - ahead.y < minGap) {
          ahead.y = behind.y - minGap;
          behind.speed = Math.min(behind.speed, Math.max(this.config.minSpeed, ahead.speed - 45));
          behind.targetSpeed = Math.min(behind.targetSpeed, behind.speed);
        }
      }
    }
  }

  private enforcePlayerRearSpacing(): void {
    const playerY = this.renderer.playerY();
    for (const vehicle of this.vehicles) {
      if (!this.playerOccupiesLane(vehicle.lane) || vehicle.y <= playerY) continue;

      const minGap = (vehicle.length + PLAYER_LENGTH) / 2 + this.config.trafficMinGap;
      const gap = vehicle.y - playerY;
      if (gap < minGap) {
        vehicle.y = playerY + minGap;
        vehicle.speed = Math.min(vehicle.speed, Math.max(this.config.minSpeed, this.player.speed - 70));
        vehicle.targetSpeed = Math.min(vehicle.targetSpeed, vehicle.speed);
      }
    }
  }

  private canSpawnInLane(lane: number): boolean {
    const spawnY = this.spawnYForLane(lane);
    return this.vehicles.every((vehicle) => {
      if (vehicle.lane !== lane) return true;
      const minGap = (vehicle.length + PLAYER_LENGTH) / 2 + this.config.spawnMinGap;
      return Math.abs(vehicle.y - spawnY) > minGap;
    });
  }

  private spawnYForLane(lane: number): number {
    const topMost = this.vehicles
      .filter((vehicle) => vehicle.lane === lane)
      .reduce((top, vehicle) => Math.min(top, vehicle.y), -240);

    return topMost - this.config.spawnMinGap - Math.random() * 210;
  }

  private finishRun(): void {
    this.score.bestScore = Math.max(this.score.bestScore, Math.floor(this.score.score));
    window.localStorage.setItem(BEST_SCORE_KEY, String(this.score.bestScore));
    this.setState("gameOver");
  }

  private reset(active: boolean): void {
    this.player = this.createPlayer();
    this.score = this.createScore();
    this.vehicles = [];
    this.spawnTimer = active ? 0.55 : 1;
    this.roadOffset = 0;
    this.elapsed = 0;
    this.flash = 0;
    this.nextVehicleId = 1;
    this.lanePressure = [0, 0, 0, 0];
    this.seedOpeningTraffic();
    this.ui.updateHud(
      this.speedKmh(),
      this.score,
      this.lanePressure,
      this.player.lives,
      this.player.maxLives
    );
  }

  private seedOpeningTraffic(): void {
    for (let i = 0; i < 6; i += 1) {
      const lane = i % this.config.laneCount;
      const type: VehicleType = i % 4 === 0 ? "suv" : i % 3 === 0 ? "van" : "sedan";
      const dimensions = this.vehicleDimensions(type);
      this.vehicles.push({
        id: this.nextVehicleId,
        lane,
        y: -360 - i * 185 - Math.random() * 120,
        speed: 270 + Math.random() * 290,
        targetSpeed: 270 + Math.random() * 290,
        length: dimensions.length,
        width: dimensions.width,
        type,
        reactionDelay: 1 + Math.random(),
        reactionTimer: Math.random(),
        behavior: i % 5 === 0 ? "slow" : "steady",
        color: COLORS[this.nextVehicleId % COLORS.length],
        passed: false,
        nearMissed: false
      });
      this.nextVehicleId += 1;
    }
  }

  private createPlayer(): Player {
    const lane = 1;
    return {
      lane,
      targetLane: lane,
      visualX: this.renderer?.laneCenter(lane) ?? 0,
      speed: this.config.cruiseSpeed,
      lives: MAX_LIVES,
      maxLives: MAX_LIVES,
      damageCooldown: 0,
      boostCooldown: 0,
      boostTimer: 0,
      collisionState: 0,
      laneTransition: 0
    };
  }

  private createScore(): ScoreState {
    const stored = Number(window.localStorage.getItem(BEST_SCORE_KEY) ?? 0);
    return {
      score: 0,
      combo: 1,
      bestScore: Number.isFinite(stored) ? stored : 0,
      distance: 0,
      overtakes: 0,
      nearMisses: 0,
      penalties: 0
    };
  }

  private setState(state: GameState): void {
    this.state = state;
    this.ui.setState(state, this.score);
  }

  private applyDamage(flashStrength: number, speedPenalty: number): boolean {
    if (this.player.damageCooldown > 0 || this.state !== "playing") {
      this.flash = Math.max(this.flash, flashStrength * 0.55);
      return false;
    }

    this.player.lives = Math.max(0, this.player.lives - 1);
    this.player.damageCooldown = DAMAGE_COOLDOWN;
    this.player.speed = Math.max(this.config.minSpeed, this.player.speed - speedPenalty);
    this.flash = Math.max(this.flash, flashStrength);

    this.ui.updateHud(
      this.speedKmh(),
      this.score,
      this.lanePressure,
      this.player.lives,
      this.player.maxLives
    );

    if (this.player.lives <= 0) {
      this.finishRun();
    }

    return true;
  }

  private speedKmh(): number {
    return 35 + (this.player.speed / this.config.maxSpeed) * 125;
  }

  private easeOutCubic(value: number): number {
    return 1 - (1 - value) ** 3;
  }

  private approachSpeed(
    current: number,
    target: number,
    accelerationRate: number,
    brakingRate: number,
    dt: number
  ): number {
    const delta = target - current;
    if (Math.abs(delta) < 0.001) return target;

    const rate = delta > 0 ? accelerationRate : brakingRate;
    const easingDistance = 180;
    const easedRate = rate * this.clamp(Math.abs(delta) / easingDistance, 0.35, 1);
    const maxDelta = easedRate * dt;
    if (Math.abs(delta) <= maxDelta) return target;

    return current + Math.sign(delta) * maxDelta;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }
}
